import { useRef, useState } from 'react';

import loadable from '@loadable/component';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-up';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import DesktopScrollContainer from '../common/DesktopScrollContainer/DesktopScrollContainer';
import FooterBlurb from '../common/FooterBlurb';
import { useMBDContext } from '../common/MBD/useMBDContext';

import DestinationContainer from './DestinationContainer';

import type {
  PopularDestination,
  PopularDestinationsProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/PopularDestinationsProps';

import STYLES from './PopularDestinations.module.scss';

const MOBILE_CARD_LIST_LIMIT = 3;

const ChevronDownArrow = withRtlSupport(ChevronDownIcon);
const ChevronUpArrow = withRtlSupport(ChevronUpIcon);

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

type SelectedChipsProps = {
  activeChipIndex: number;
  componentName: string;
  setActiveChipIndex: (index: number) => void;
  chips: string[];
  resetPagination: () => void;
};

const SelectedChips = ({
  activeChipIndex,
  chips,
  componentName,
  resetPagination,
  setActiveChipIndex,
}: SelectedChipsProps) => {
  const { buildMBDParam } = useMBDContext();
  const mbdComponentName = `${componentName}_item`;
  if (!chips || !chips.length) {
    return null;
  }

  return (
    <div className={STYLES.SelectableChip}>
      <BpkMobileScrollContainer>
        {chips.map(
          (chip, index) =>
            chip && (
              <div key={chip} className={STYLES.SelectableChip__item}>
                <BpkSelectableChip
                  key={chip}
                  accessibilityLabel={chip}
                  selected={index === activeChipIndex}
                  onClick={() => {
                    setActiveChipIndex(index);
                    resetPagination();
                  }}
                  data-tracking-element-id={`${mbdComponentName}_${index}`}
                  data-tracking-common-params={buildMBDParam(mbdComponentName, {
                    tab_name: chip,
                  })}
                >
                  {chip}
                </BpkSelectableChip>
              </div>
            ),
        )}
      </BpkMobileScrollContainer>
    </div>
  );
};

const PopularDestinations = (props: PopularDestinationsProps) => {
  const {
    disclaimer,
    grouping = false,
    mobileExpandable,
    mobileScroll,
    noShowImage,
    popularDestinations,
    relFollow,
    showAll,
    stringSources,
    subtitle,
    title,
  } = props;
  const from = stringSources?.from ?? '';
  const perDay = stringSources?.perDay ?? '';

  const [activeChipIndex, setActiveChipIndex] = useState(0);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const paginationRef = useRef({ resetPagination: () => {} });
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const componentName = props.componentName || rawComponentName;

  const chips = grouping
    ? Array.from(
        new Set(
          popularDestinations
            .map((item) => item.groupKey)
            .filter((key): key is string => !!key),
        ),
      )
    : [];

  const renderDestinations = grouping
    ? popularDestinations.filter(
        (destination) => chips[activeChipIndex] === destination.groupKey,
      )
    : popularDestinations;

  if (!title || !renderDestinations?.length) {
    return null;
  }

  const mobileCardLimit =
    mobileExpandable?.maxToShowWhenCollapse ?? MOBILE_CARD_LIST_LIMIT;
  const mobileDestinationsToRender =
    mobileExpandable && isMobileExpanded
      ? renderDestinations
      : renderDestinations.slice(0, mobileCardLimit);
  const showMobileExpandButton =
    !!mobileExpandable &&
    renderDestinations.length > mobileExpandable.maxToShowWhenCollapse;
  const mobileCardListId = `${componentName}-mobile-card-list`;

  return (
    <div
      className={STYLES.PopularDestinations}
      data-tracking-element-id={componentName}
      data-tracking-common-params={buildMBDParam(componentName)}
    >
      <div className={STYLES.PopularDestinations__header}>
        <BpkSectionHeader title={title} description={subtitle} />
      </div>
      <SelectedChips
        chips={chips}
        componentName={componentName}
        activeChipIndex={activeChipIndex}
        setActiveChipIndex={setActiveChipIndex}
        resetPagination={paginationRef.current?.resetPagination ?? (() => {})}
      />
      {showAll ? (
        <div className={STYLES.PopularDestinations__desktop}>
          <div className={STYLES.PopularDestinations__cardlist}>
            {renderDestinations.map(
              (destination: PopularDestination, index: number) => (
                <div
                  key={`${destination.subtitle}_${index + 1}`}
                  className={STYLES.PopularDestinations__cardlist__container}
                >
                  <DestinationContainer
                    destination={destination}
                    from={from}
                    perDay={perDay}
                    index={index}
                    componentName={componentName}
                    relFollow={relFollow}
                    noShowImage={noShowImage}
                  />
                </div>
              ),
            )}
          </div>
        </div>
      ) : (
        <DesktopScrollContainer
          nextLabel="next"
          prevLabel="prev"
          ref={paginationRef}
        >
          {renderDestinations.map(
            (destination: PopularDestination, index: number) => (
              <div
                key={`${destination.subtitle}_${index + 1}`}
                className={STYLES.PopularDestinations__scroll__container}
              >
                <DestinationContainer
                  destination={destination}
                  from={from}
                  perDay={perDay}
                  index={index}
                  componentName={componentName}
                  relFollow={relFollow}
                  noShowImage={noShowImage}
                />
              </div>
            ),
          )}
        </DesktopScrollContainer>
      )}
      {mobileScroll ? (
        <BpkMobileScrollContainer
          className={STYLES.PopularDestinations__mobile}
        >
          <div className={STYLES.PopularDestinations__scroll__list}>
            {renderDestinations &&
              renderDestinations.map(
                (destination: PopularDestination, index: number) => (
                  <div
                    key={`${destination.subtitle}_${index + 1}`}
                    className={STYLES.PopularDestinations__scroll__container}
                  >
                    <DestinationContainer
                      destination={destination}
                      from={from}
                      perDay={perDay}
                      index={index}
                      componentName={componentName}
                      relFollow={relFollow}
                      mobileScroll={mobileScroll}
                      noShowImage={noShowImage}
                    />
                  </div>
                ),
              )}
          </div>
        </BpkMobileScrollContainer>
      ) : (
        <div className={STYLES.PopularDestinations__mobile}>
          <div
            id={mobileCardListId}
            className={STYLES.PopularDestinations__mobileCardList}
          >
            {mobileDestinationsToRender.map(
              (destination: PopularDestination, index: number) => (
                <div
                  key={`${destination.subtitle}_${index + 1}`}
                  className={
                    STYLES.PopularDestinations__mobileCardList__container
                  }
                >
                  <DestinationContainer
                    destination={destination}
                    from={from}
                    perDay={perDay}
                    index={index}
                    componentName={componentName}
                    relFollow={relFollow}
                    mobileScroll={mobileScroll}
                    noShowImage={noShowImage}
                  />
                </div>
              ),
            )}
          </div>
          {showMobileExpandButton && mobileExpandable && (
            <div className={STYLES.PopularDestinations__mobileExpandButton}>
              <BpkButton
                onClick={() => setIsMobileExpanded((prev) => !prev)}
                type={BUTTON_TYPES.link}
                implicit
                aria-expanded={isMobileExpanded}
                aria-controls={mobileCardListId}
                data-testid="popular-destinations-mobile-expand-toggle"
                data-tracking-element-id={`${componentName}_item`}
                data-tracking-common-params={buildMBDParam(
                  `${componentName}_item`,
                  { action: isMobileExpanded ? 'collapse' : 'expand' },
                )}
              >
                <BpkText textStyle={TEXT_STYLES.label1}>
                  {isMobileExpanded
                    ? mobileExpandable.collapseText
                    : mobileExpandable.expandText}
                </BpkText>
                {isMobileExpanded ? <ChevronUpArrow /> : <ChevronDownArrow />}
              </BpkButton>
            </div>
          )}
        </div>
      )}
      {disclaimer && (
        <div className={STYLES.PopularDestinations__footer}>
          <FooterBlurb disclaimerCopy={disclaimer} />
        </div>
      )}
    </div>
  );
};

export default (props: PopularDestinationsProps) => (
  <IntersectionObserverWrapper
    threshold={0.5}
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name:
          props.componentName || COMPONENT_NAME.POPULAR_DESTINATIONS,
      })
    }
  >
    <PopularDestinations {...props} />
  </IntersectionObserverWrapper>
);
